import React, { FormEvent, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { Link, useSearch } from '@tanstack/react-location';
import { resetPasswordRequest } from '../requests/session.request';

const PASSWORD_RULES =
  'At least 10 characters, with an uppercase letter, a lowercase letter and a digit.';

// Brand colors matching wefighttickets.com
const colors = {
  orange: '#fc6f23',
  orangeHover: '#d8531a',
  darkInk: '#191b1f',
  mutedGray: '#5c606b',
  border: '#ece8e2',
  cream: '#faf8f5',
  white: '#ffffff'
};

const styles = {
  pageBackground: {
    minHeight: '100vh',
    backgroundColor: colors.cream,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  } as React.CSSProperties,
  card: {
    maxWidth: 440,
    width: '100%',
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: 20,
    boxShadow: '0 30px 60px -42px rgba(0,0,0,0.15)',
    padding: 30
  } as React.CSSProperties,
  logo: {
    textAlign: 'center',
    marginBottom: 24
  } as React.CSSProperties,
  logoText: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.darkInk,
    margin: 0
  } as React.CSSProperties,
  logoAccent: {
    color: colors.orange
  } as React.CSSProperties,
  heading: {
    fontSize: 20,
    fontWeight: 600,
    color: colors.darkInk,
    textAlign: 'center',
    marginBottom: 24
  } as React.CSSProperties,
  label: {
    fontSize: '.76rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: colors.mutedGray,
    marginBottom: 8
  } as React.CSSProperties,
  input: {
    padding: '13px 14px',
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    fontSize: 16,
    backgroundColor: colors.white
  } as React.CSSProperties,
  submitBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: 12,
    fontWeight: 800,
    fontSize: 16,
    backgroundColor: colors.orange,
    borderColor: colors.orange,
    marginTop: 8
  } as React.CSSProperties,
  backLink: {
    fontSize: 14,
    color: colors.orange,
    textDecoration: 'none'
  } as React.CSSProperties,
  successAlert: {
    backgroundColor: '#e8f5e9',
    borderColor: '#c8e6c9',
    color: '#2e7d32',
    borderRadius: 10,
    padding: '16px',
    marginBottom: 16
  } as React.CSSProperties,
  errorAlert: {
    borderRadius: 10,
    padding: '16px',
    marginBottom: 16
  } as React.CSSProperties,
  helperText: {
    fontSize: 12,
    color: colors.mutedGray,
    marginTop: 6
  } as React.CSSProperties
};

function ResetPasswordPage() {
  const { token } = useSearch() as { token?: string };

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setErrors(['The passwords do not match.']);
      return;
    }
    setErrors([]);
    setSubmitting(true);
    try {
      await resetPasswordRequest(token || '', newPassword);
      setSuccess(true);
    } catch (e) {
      const data = e?.response?.data;
      setErrors([data?.message ?? 'Password reset failed.', ...(data?.details ?? [])]);
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div style={styles.pageBackground}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <h1 style={styles.logoText}>
              WE FIGHT <span style={styles.logoAccent}>TICKETS</span>
            </h1>
          </div>
          <h2 style={styles.heading}>Invalid Link</h2>
          <Alert variant="danger" style={styles.errorAlert}>
            Invalid or missing reset token. Please request a new password reset link.
          </Alert>
          <Link to="/forgot-password">
            <Button style={styles.submitBtn}>Request New Link</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageBackground}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <h1 style={styles.logoText}>
            WE FIGHT <span style={styles.logoAccent}>TICKETS</span>
          </h1>
        </div>
        <h2 style={styles.heading}>Create new password</h2>
        {success ? (
          <>
            <Alert style={styles.successAlert}>
              Your password has been reset. You can now sign in with your new password.
            </Alert>
            <Link to="/login">
              <Button style={styles.submitBtn}>Sign In</Button>
            </Link>
          </>
        ) : (
          <>
            <Form onSubmit={onSubmit}>
              {errors.length > 0 && (
                <Alert variant="danger" style={styles.errorAlert}>
                  {errors.map((err) => (
                    <div key={err}>{err}</div>
                  ))}
                </Alert>
              )}
              <Form.Group className="mb-3" controlId="newPassword">
                <Form.Label style={styles.label}>New Password</Form.Label>
                <Form.Control
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                  style={styles.input}
                />
                <div style={styles.helperText}>{PASSWORD_RULES}</div>
              </Form.Group>
              <Form.Group className="mb-3" controlId="confirmPassword">
                <Form.Label style={styles.label}>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={styles.input}
                />
              </Form.Group>
              <Button type="submit" disabled={submitting} style={styles.submitBtn}>
                {submitting ? <Spinner animation="border" size="sm" /> : 'Reset Password'}
              </Button>
            </Form>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Link to="/login" style={styles.backLink}>
                Back to login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordPage;
